/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { Header } from './header';
import { Footer } from './footer';
import { PageConfigurator } from '../../configurator/page';
import { I18n } from '../../../survey.types';
import { SurveyContextType } from '../../../survey-context';

interface PageViewProps {
  isFirstPage: boolean,
  isLastPage: boolean,
  isEndingPage: boolean,
  onSubmit: any,
  onDone: any,
  onNext: any,
  onDismiss: any,
  onBack: any,
  classes: any,
  configurator: PageConfigurator,
  answers: any,
  setAnswers: any,
}

const SurveyQuestionComponents = React.memo((props: {
  configurator: PageConfigurator,
  classes: any,
}) => {
  const {
    classes,
    configurator,
  } = props;
  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);

  const {
    getAnswersByKey,
    setAnswersByKey,
    getSurveyAnswers,
    getSurveyDirtyStateByKey,
    setSurveyDirtyStateByKey,
  } = surveyContext;

  const answers = getSurveyAnswers();

  return (
    <div className={classNames(classes.body, 'survey-body')}>
      {configurator.questions.map((question) => {
        const isVisible = question.isVisible(answers);
        const Question = useMemo(() => question.getView(), [question]);
        const { key } = question;
        return (
          <div className="question-block" key={key} style={{ display: isVisible ? 'block' : 'none' }}>
            <Question
              isVisible={isVisible}
              key={key}
              data={getAnswersByKey(key)}
              setData={data => setAnswersByKey(key, data)}
              dirtyState={getSurveyDirtyStateByKey(key)}
              setDirtyState={isDirty => setSurveyDirtyStateByKey(key, isDirty)}
            />
          </div>
        );
      })}
    </div>
  );
});

export const PageView = React.memo((props: PageViewProps) => {
  const {
    classes,
  } = props;

  const i18n: I18n = useContext(I18nContext);

  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    getIsFirstPage,
    getIsEndingPage,
    getIsLastPage,
    onDismiss,
    onBack,
    onDone,
    onSubmit,
    onNext,
    getIsSubmitButtonDisabled,
    getIsFromBack,
  } = surveyContext;

  const isFirstPage = getIsFirstPage();
  const isEndingPage = getIsEndingPage();
  const isLastPage = getIsLastPage();
  const isSubmitButtonDisabled = getIsSubmitButtonDisabled();
  const firstFocusableElement: any = useRef(null);
  const lastFocusableElement: any = useRef(null);
  const isFromBack = getIsFromBack();

  useEffect(() => {
    if (isFromBack) {
      lastFocusableElement?.current?.focus?.();
      return;
    }
    firstFocusableElement?.current?.focus?.();
  }, []);

  return (
    <div className="Page">
      <Header
        isFirstPage={isFirstPage}
        isEndingPage={isEndingPage}
        onDismiss={onDismiss}
        classes={classes}
        onBack={onBack}
        onDone={onDone}
        ref={firstFocusableElement}
      />
      {isEndingPage ?
        <span>
          {i18n.getString(_TL_('Thank you so much for the response. We will continue improve our products and services.'))}
        </span> : <SurveyQuestionComponents {...props} />
      }
      <Footer
        isSubmitButtonDisabled={isSubmitButtonDisabled}
        isLastPage={isLastPage}
        isEndingPage={isEndingPage}
        onSubmit={onSubmit}
        onNext={onNext}
        onDone={onDone}
        onDismiss={onDismiss}
        ref={lastFocusableElement}
      />
    </div>
  );
});
